import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!(newPrice instanceof HTMLElement) || !(currentPrice instanceof HTMLElement)) return;

    const nextPrice = /** @type {HTMLElement} */ (newPrice.cloneNode(true));
    const currentTaxLabel = currentPrice.querySelector('.price-tax-label');
    const nextTaxLabel = nextPrice.querySelector('.price-tax-label');
    const showSalePriceFirst = currentPrice.dataset.showSalePriceFirst;
    const shouldShowTaxLabel =
      currentPrice.dataset.showTaxLabel === 'true' ||
      (currentPrice.dataset.showTaxLabel === undefined && Boolean(currentTaxLabel));

    // Reconcile the fetched price markup with the current card context so tax labels
    // do not leak into unrelated components or disappear after variant changes.
    if (shouldShowTaxLabel && currentTaxLabel && !nextTaxLabel) {
      const priceElement = nextPrice.querySelector('.price');

      if (priceElement) {
        priceElement.insertAdjacentElement('afterend', /** @type {Element} */ (currentTaxLabel.cloneNode(true)));
      } else {
        nextPrice.append(currentTaxLabel.cloneNode(true));
      }
    } else if (!shouldShowTaxLabel && nextTaxLabel) {
      nextTaxLabel.remove();
    }

    nextPrice.dataset.showTaxLabel = shouldShowTaxLabel ? 'true' : 'false';
    if (showSalePriceFirst === 'true' || showSalePriceFirst === 'false') {
      nextPrice.dataset.showSalePriceFirst = showSalePriceFirst;

      if (nextPrice.classList.contains('price-container--on-sale')) {
        const groups = Array.from(nextPrice.querySelectorAll(':scope > span[role="group"]'));
        const saleGroup = groups.find((group) => group.querySelector('.price'));
        const compareGroup = groups.find((group) => group.querySelector('.compare-at-price'));

        if (saleGroup && compareGroup) {
          if (showSalePriceFirst === 'true') {
            nextPrice.prepend(saleGroup);
            saleGroup.insertAdjacentElement('afterend', compareGroup);
          } else {
            nextPrice.prepend(compareGroup);
            compareGroup.insertAdjacentElement('afterend', saleGroup);
          }
        }
      }
    }

    if (currentPrice.innerHTML !== nextPrice.innerHTML) {
      currentPrice.replaceWith(nextPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
